import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Crown, BellRing, Bell, Check, Send, Loader2, Pin, ShieldCheck, Users, Trash2, Sparkles, Lock,
  Paperclip, Smile, BarChart3, Image as ImageIcon, Video, FileText, Mic, X, Plus, Square, Download, Eye, Zap, Trophy, ChevronDown, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { istTime } from "../utils/time";
import { enablePush, disablePush, pushSupported } from "../utils/push";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const tsOf = (iso) => (iso ? new Date(iso).getTime() / 1000 : 0);
const mediaUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);
const fmtSize = (b) => (!b ? "" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const EMOJIS = "🔥🎯✅🟢🔴📈📉💰🚀⚡️⚠️👀💎🏆🙌👌📊🕒💵🐂🐻❤️😀😎🤝🙏👏💪🎉📌🔔📍✍️💡🧠⏰🎁💯👍".split(/(?=)/u).filter((e) => e.trim());

const RoleBadge = ({ role }) => (
  <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold inline-flex items-center gap-1"
    style={{ color: role === "Owner" ? "#e6c25a" : "#8fd3ff", background: "rgba(255,255,255,0.06)" }}>
    {role === "Owner" ? <Crown className="w-2.5 h-2.5" /> : <ShieldCheck className="w-2.5 h-2.5" />}{role}
  </span>
);
const Rx = ({ emoji, count, on, onClick }) => (
  <button onClick={onClick} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] transition-colors ${on ? "bg-[rgba(230,194,90,0.22)] text-white" : "bg-[rgba(255,255,255,0.06)] text-[color:var(--ws-muted)] hover:text-white"}`}>
    <span className="text-[13px] leading-none">{emoji}</span> {count}
  </button>
);

const Poll = ({ poll, onVote }) => {
  const total = poll.total || 0;
  return (
    <div className="ws-tg-poll" data-testid="vip-poll">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[color:var(--ws-blue-bright)] mb-1"><BarChart3 className="w-3 h-3" /> Poll</div>
      <div className="ws-heading font-semibold text-white text-[14px] mb-2.5">{poll.question}</div>
      <div className="space-y-2">
        {poll.options.map((o, i) => {
          const pct = total ? Math.round((o.count / total) * 100) : 0;
          const mine = poll.my_vote === i;
          return (
            <button key={i} onClick={() => onVote(i)} data-testid={`poll-opt-${i}`} className="ws-tg-poll-opt">
              <div className="ws-tg-poll-fill" style={{ width: `${pct}%`, background: mine ? "rgba(230,194,90,0.28)" : "rgba(59,130,246,0.22)" }} />
              <span className="relative flex items-center gap-1.5 text-[13px] text-white">{mine && <Check className="w-3.5 h-3.5 text-[#e6c25a]" />}{o.text}</span>
              <span className="relative text-[12px] text-[color:var(--ws-muted)] font-semibold">{pct}%</span>
            </button>
          );
        })}
      </div>
      <div className="text-[11px] text-[color:var(--ws-muted)] mt-2">{total} vote{total === 1 ? "" : "s"} · tap to vote</div>
    </div>
  );
};

const Media = ({ m, onOpenImage }) => {
  const url = mediaUrl(m.media?.url);
  if (m.type === "image") return <img src={url} alt={m.media?.name} onClick={() => onOpenImage?.(url)} className="ws-tg-media-img cursor-zoom-in" loading="lazy" data-testid="vip-image" />;
  if (m.type === "video") return <video src={url} controls className="ws-tg-media-img" />;
  if (m.type === "voice") return <audio src={url} controls className="ws-tg-audio" />;
  return (
    <a href={url} target="_blank" rel="noreferrer" download={m.media?.name} className="ws-tg-doc" data-testid="vip-doc">
      <div className="ws-tg-doc-ic"><FileText className="w-5 h-5" /></div>
      <div className="flex-1 min-w-0"><div className="text-[13px] font-medium text-white truncate">{m.media?.name}</div><div className="text-[11px] text-[color:var(--ws-muted)]">{fmtSize(m.media?.size)}</div></div>
      <Download className="w-4 h-4 text-[color:var(--ws-muted)]" />
    </a>
  );
};

const MsgBubble = ({ m, isAdmin, onReact, onDelete, onVote, onOpenImage }) => (
  <div className="flex gap-2.5" data-testid={`vip-msg-${m.msg_id}`}>
    <div className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center self-end" style={{ background: "linear-gradient(135deg,#e6c25a,#b08d57)" }}><Crown className="w-4 h-4 text-white" /></div>
    <div className="flex-1 min-w-0">
      <div className={`ws-tg-bubble ${m.pinned ? "pinned" : ""}`}>
        {m.pinned && <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-[#e6c25a] mb-1.5"><Pin className="w-3 h-3" /> Pinned</div>}
        <div className="flex items-center gap-2 mb-1.5">
          <span className="ws-heading font-semibold text-[13px]" style={{ color: "#7cc7ff" }}>{m.author_name}</span>
          <RoleBadge role={m.role} />
          {isAdmin && <button onClick={() => onDelete(m.msg_id)} className="ml-auto text-[color:var(--ws-muted)] hover:text-[color:var(--ws-red)]" data-testid={`vip-del-${m.msg_id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
        </div>
        {(m.type === "image" || m.type === "video" || m.type === "voice" || m.type === "document") && m.media && <div className="mb-2"><Media m={m} onOpenImage={onOpenImage} /></div>}
        {m.type === "poll" && m.poll && <div className="mb-1"><Poll poll={m.poll} onVote={(i) => onVote(m, i)} /></div>}
        {m.text && <p className="text-[14px] text-[color:var(--ws-text)] opacity-95 whitespace-pre-wrap leading-relaxed break-words">{m.text}</p>}
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {[["heart", "❤️"], ["fire", "🔥"], ["target", "🎯"]].map(([t, e]) => (
            <Rx key={t} emoji={e} count={m.reactions?.[t] || 0} on={m.my_reactions?.includes(t)} onClick={() => onReact(m, t)} />
          ))}
          <span className="ml-auto flex items-center gap-1 text-[11px] text-[color:var(--ws-muted)]"><Eye className="w-3 h-3" /> {m.views} · {istTime(tsOf(m.created_at))} IST</span>
        </div>
      </div>
    </div>
  </div>
);

const WSVIPCommunity = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [pollOpen, setPollOpen] = useState(false);
  const [pollQ, setPollQ] = useState("");
  const [pollOpts, setPollOpts] = useState(["", ""]);
  const [recording, setRecording] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [autoOn, setAutoOn] = useState(true);
  const [posOpen, setPosOpen] = useState(false);
  const [positions, setPositions] = useState(null);
  const [posBusy, setPosBusy] = useState("");
  const [autoGive, setAutoGive] = useState(false);
  const toggleAutoGive = async () => {
    const next = !autoGive; setAutoGive(next);
    try {
      const r = await (await fetch(`${API}/vip/give-config`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ enabled: next }) })).json();
      setAutoGive(!!r.enabled);
      toast.success(r.enabled ? "Auto-Give ON ⚡ — new MT5 positions post to VIP automatically" : "Auto-Give off");
    } catch { setAutoGive(!next); toast.error("Could not update"); }
  };
  const openPositions = async () => {
    setPosOpen(true); setPositions(null);
    try {
      const r = await (await fetch(`${API}/mt5/positions`, { credentials: "include" })).json();
      setPositions(r.positions || []);
    } catch { setPositions([]); }
  };
  const givePosition = async (p) => {
    const isBuy = String(p.type ?? "").toLowerCase().includes("buy") || String(p.type) === "0" || String(p.type).toLowerCase() === "long";
    setPosBusy(p.ticket);
    try {
      const r = await (await fetch(`${API}/vip/post-position`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ticket: p.ticket, symbol: (p.symbol || "").split(":").pop(), type: isBuy ? "buy" : "sell", entry: p.price_open, tp: p.tp, sl: p.sl, target: "vip" }),
      })).json();
      if (r.ok) { toast.success("Entry posted to VIP ✅"); setPosOpen(false); } else { toast.error(r.detail || "Could not post."); }
    } catch { toast.error("Could not post entry."); }
    setPosBusy("");
  };
  const [board, setBoard] = useState(null);
  const [boardOpen, setBoardOpen] = useState(false);
  const [boardDays, setBoardDays] = useState(7);
  const endRef = useRef(null);
  const imgIn = useRef(null); const vidIn = useRef(null); const docIn = useRef(null);
  const recRef = useRef(null); const chunks = useRef([]);

  const load = useCallback(async (seen) => {
    try { const d = await (await fetch(`${API}/vip/channel${seen ? "?seen=1" : ""}`, { credentials: "include" })).json(); setData(d); }
    catch { /* */ } setLoading(false);
  }, []);
  useEffect(() => { load(true); }, [load]);
  const loadBoard = useCallback(async () => {
    try { const b = await (await fetch(`${API}/vip/scoreboard?days=${boardDays}`, { credentials: "include" })).json(); setBoard(b); } catch { /* */ }
  }, [boardDays]);
  useEffect(() => { loadBoard(); const t = setInterval(loadBoard, 60000); return () => clearInterval(t); }, [loadBoard]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);
  useEffect(() => { const t = setInterval(() => load(false), 12000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (data?.notifications && pushSupported() && Notification.permission === "granted") enablePush().catch(() => {}); }, [data?.notifications]);
  useEffect(() => {
    if (!data?.is_admin) return;
    fetch(`${API}/vip/auto-config`, { credentials: "include" }).then((r) => r.json()).then((r) => setAutoOn(!!r.enabled)).catch(() => {});
    fetch(`${API}/vip/give-config`, { credentials: "include" }).then((r) => r.json()).then((r) => setAutoGive(!!r.enabled)).catch(() => {});
  }, [data?.is_admin]);
  const toggleAuto = async () => {
    const next = !autoOn; setAutoOn(next);
    try {
      const r = await (await fetch(`${API}/vip/auto-config`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ enabled: next }) })).json();
      setAutoOn(!!r.enabled);
      toast.success(r.enabled ? "Auto trade updates ON ⚡ — signals auto-post 1:1 / TP hits" : "Auto trade updates off");
    } catch { setAutoOn(!next); toast.error("Could not update"); }
  };

  const patch = (f) => setData((d) => ({ ...d, ...f }));

  const toggleSub = async () => {
    try { const r = await (await fetch(`${API}/vip/subscribe`, { method: "POST", credentials: "include" })).json();
      patch({ subscribed: r.subscribed, notifications: r.notifications, channel: { ...data.channel, subscriber_count: r.subscriber_count } });
      toast.success(r.subscribed ? "Subscribed to WS VIP" : "Unsubscribed");
    } catch { toast.error("Could not update."); }
  };
  const toggleNotif = async () => {
    if (!data.subscribed) { toast.error("Subscribe first to manage notifications."); return; }
    const turningOn = !data.notifications;
    if (turningOn) { try { await enablePush(); } catch (e) { if (e.message === "denied") { toast.error("Allow notifications in your browser."); return; } toast.message("Browser push isn't available here — preference saved."); } }
    else await disablePush();
    try { const r = await (await fetch(`${API}/vip/notifications`, { method: "POST", credentials: "include" })).json(); patch({ notifications: r.notifications }); toast.success(r.notifications ? "Push notifications on 🔔" : "Notifications off"); }
    catch { toast.error("Could not update."); }
  };
  const send = async () => {
    const t = draft.trim(); if (!t) return; setSending(true);
    try { const r = await (await fetch(`${API}/vip/message`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ type: "text", text: t }) })).json();
      if (r.message) { patch({ messages: [...(data.messages || []), r.message] }); setDraft(""); } } catch { toast.error("Could not broadcast."); }
    setSending(false);
  };
  const uploadAndSend = async (file, type) => {
    setAttachOpen(false);
    const fd = new FormData(); fd.append("file", file);
    const tid = toast.loading("Uploading…");
    try {
      const up = await (await fetch(`${API}/vip/upload`, { method: "POST", credentials: "include", body: fd })).json();
      if (!up.url) { toast.error(up.detail || "Upload failed", { id: tid }); return; }
      const r = await (await fetch(`${API}/vip/message`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ type, text: draft.trim(), media: { url: up.url, name: up.name, mime: up.mime, size: up.size } }) })).json();
      if (r.message) { patch({ messages: [...(data.messages || []), r.message] }); setDraft(""); toast.success("Broadcast sent", { id: tid }); }
    } catch { toast.error("Upload failed", { id: tid }); }
  };
  const pickFile = (ref, type) => { const f = ref.current?.files?.[0]; if (f) uploadAndSend(f, type); if (ref.current) ref.current.value = ""; };
  const sendPoll = async () => {
    const opts = pollOpts.map((o) => o.trim()).filter(Boolean);
    if (!pollQ.trim() || opts.length < 2) { toast.error("Add a question and 2+ options."); return; }
    try { const r = await (await fetch(`${API}/vip/message`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ type: "poll", poll: { question: pollQ, options: opts } }) })).json();
      if (r.message) { patch({ messages: [...(data.messages || []), r.message] }); setPollOpen(false); setPollQ(""); setPollOpts(["", ""]); } } catch { toast.error("Could not create poll."); }
  };
  const vote = async (m, i) => {
    try { const r = await (await fetch(`${API}/vip/message/${m.msg_id}/vote`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ option: i }) })).json();
      if (r.poll) patch({ messages: data.messages.map((x) => x.msg_id === m.msg_id ? { ...x, poll: r.poll } : x) }); } catch { /* */ }
  };
  const react = async (m, type) => {
    patch({ messages: data.messages.map((x) => { if (x.msg_id !== m.msg_id) return x; const has = x.my_reactions?.includes(type); const my = has ? x.my_reactions.filter((t) => t !== type) : [...(x.my_reactions || []), type]; return { ...x, my_reactions: my, reactions: { ...x.reactions, [type]: (x.reactions?.[type] || 0) + (has ? -1 : 1) } }; }) });
    try { await fetch(`${API}/vip/message/${m.msg_id}/react`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ type }) }); } catch { load(false); }
  };
  const del = async (id) => { patch({ messages: data.messages.filter((x) => x.msg_id !== id) }); try { await fetch(`${API}/vip/message/${id}`, { method: "DELETE", credentials: "include" }); toast.success("Deleted."); } catch { load(false); } };
  const testPush = async () => {
    try { const res = await fetch(`${API}/push/test`, { method: "POST", credentials: "include" }); const r = await res.json();
      if (!res.ok) { toast.error(r.detail || "Turn on notifications first."); return; }
      toast.success(r.sent > 0 ? `Test alert sent to ${r.sent} device${r.sent > 1 ? "s" : ""} 🔔` : "No device received it — tap the bell on this device."); } catch { toast.error("Could not send test."); }
  };
  const startRec = async () => {
    try { const stream = await navigator.mediaDevices.getUserMedia({ audio: true }); const mr = new MediaRecorder(stream); recRef.current = mr; chunks.current = [];
      mr.ondataavailable = (e) => chunks.current.push(e.data);
      mr.onstop = () => { const blob = new Blob(chunks.current, { type: "audio/webm" }); stream.getTracks().forEach((t) => t.stop()); uploadAndSend(new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" }), "voice"); };
      mr.start(); setRecording(true); setAttachOpen(false);
    } catch { toast.error("Microphone permission needed."); }
  };
  const stopRec = () => { try { recRef.current?.stop(); } catch { /* */ } setRecording(false); };

  if (loading) return <div className="max-w-[860px] mx-auto px-8 py-16 text-center text-[color:var(--ws-muted)]"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!data) return null;
  const c = data.channel;
  const pinned = (data.messages || []).find((m) => m.pinned);

  return (
    <div className="ws-tg-wrap">
      {/* Channel header */}
      <div className="ws-tg-header" data-testid="vip-header">
        <div className="ws-logo-3d w-11 h-11" data-testid="vip-logo"><img src="/wsj-logo.webp" alt="Wealth Secret Journal" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="ws-heading font-bold text-white text-[16px] truncate">{c.name}</span>
            {c.verified && <span className="ws-vip-verified"><Check className="w-3 h-3" strokeWidth={3} /></span>}
          </div>
          <div className="text-[12px] text-[color:var(--ws-muted)]">{c.subscriber_count.toLocaleString()} subscribers</div>
        </div>
        <button onClick={toggleSub} data-testid="vip-subscribe" className={`px-3.5 py-1.5 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 ${data.subscribed ? "ws-btn-ghost" : "ws-btn-primary"}`}>
          {data.subscribed ? <><Check className="w-4 h-4" /> Subscribed</> : <><Sparkles className="w-4 h-4" /> Subscribe</>}
        </button>
        <button onClick={toggleNotif} data-testid="vip-notifications" title="Notifications" className={`w-9 h-9 rounded-lg flex items-center justify-center ${data.notifications ? "bg-[rgba(230,194,90,0.2)] text-[#e6c25a]" : "ws-btn-ghost"}`}>
          {data.notifications ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
        </button>
        {data.is_admin && (
          <button onClick={toggleAuto} data-testid="vip-auto-updates" title="Auto trade updates — re-checks open signals & auto-posts 1:1 / target hit" className={`w-9 h-9 rounded-lg flex items-center justify-center ${autoOn ? "bg-[rgba(90,169,230,0.2)] text-[#5aa9e6]" : "ws-btn-ghost"}`}>
            <Zap className="w-4 h-4" fill={autoOn ? "currentColor" : "none"} />
          </button>
        )}
      </div>

      {/* Pinned bar */}
      {pinned && (
        <div className="ws-tg-pinned" data-testid="vip-pinned">
          <Pin className="w-3.5 h-3.5 text-[#e6c25a] shrink-0" />
          <div className="min-w-0"><div className="text-[10px] text-[#e6c25a] uppercase tracking-wide">Pinned</div><div className="text-[12px] text-[color:var(--ws-text)] truncate">{pinned.text || (pinned.poll?.question) || "Media"}</div></div>
        </div>
      )}

      {/* Signal Scoreboard — weekly win/loss tally of auto-tracked signals */}
      {board?.totals && (
        <div className="ws-tg-board" data-testid="vip-scoreboard">
          <button onClick={() => setBoardOpen((v) => !v)} className="ws-tg-board-head" data-testid="vip-scoreboard-toggle">
            <span className="ws-tg-board-title"><Trophy className="w-3.5 h-3.5 text-[#e6c25a]" /> {boardDays === 7 ? "This week's signals" : boardDays === 30 ? "Last 30 days" : "All-time signals"}</span>
            <span className="ws-tg-board-stats">
              <b data-testid="board-signals">{board.totals.signals}</b> signals
              <span className="ws-tg-board-dot">·</span>
              <span className="text-[color:var(--ws-green)]" data-testid="board-wins">{board.totals.wins}W</span>
              <span className="text-[color:var(--ws-red)]" data-testid="board-losses">{board.totals.losses}L</span>
              <span className="ws-tg-board-dot">·</span>
              <b data-testid="board-winrate">{board.totals.win_rate}%</b>
              <span className="ws-tg-board-dot">·</span>
              <span className={board.totals.total_r >= 0 ? "text-[color:var(--ws-green)]" : "text-[color:var(--ws-red)]"} data-testid="board-totalr">{board.totals.total_r >= 0 ? "+" : ""}{board.totals.total_r}R</span>
            </span>
            <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${boardOpen ? "rotate-180" : ""}`} />
          </button>
          {boardOpen && (
            <div className="ws-tg-board-list" data-testid="vip-scoreboard-list">
              <div className="ws-tg-board-range" data-testid="vip-scoreboard-range">
                {[{ k: 7, l: "7D" }, { k: 30, l: "30D" }, { k: 3650, l: "All" }].map((r) => (
                  <button key={r.k} onClick={() => setBoardDays(r.k)} data-testid={`board-range-${r.l.toLowerCase()}`}
                    className={`ws-tg-range-btn ${boardDays === r.k ? "active" : ""}`}>{r.l}</button>
                ))}
              </div>
              {board.signals.length === 0 && <div className="text-[12px] text-[color:var(--ws-muted)] py-2">No signals in this range yet.</div>}
              {board.signals.map((s, i) => {
                const cls = s.result === "win" ? "win" : s.result === "loss" ? "loss" : s.result === "running" ? "run" : s.result === "pending" ? "run" : "flat";
                const label = s.result === "win" ? "WIN" : s.result === "loss" ? "LOSS" : s.result === "running" ? "LIVE" : s.result === "pending" ? "SOON" : s.result === "cancelled" ? "VOID" : "FLAT";
                const rightTxt = s.result === "pending" ? "upcoming" : s.result === "cancelled" ? "void" : s.result === "running" ? (s.max_r >= 1 ? `▲ ${s.max_r}R` : "running") : `${s.r_realized > 0 ? "+" : ""}${s.r_realized}R`;
                return (
                  <div key={i} className="ws-tg-board-row" data-testid={`board-row-${i}`}>
                    <span className={`ws-tg-board-badge ${cls}`}>{label}</span>
                    <span className="font-semibold text-[color:var(--ws-text)] text-[12px]">{s.symbol}</span>
                    <span className={`text-[11px] ${s.direction === "Short" ? "text-[color:var(--ws-red)]" : "text-[color:var(--ws-green)]"}`}>{s.direction === "Short" ? "SHORT" : "LONG"}</span>
                    {s.rr != null && <span className="text-[11px] text-[color:var(--ws-muted)]">R:R 1:{s.rr}</span>}
                    <span className={`ml-auto text-[12px] font-semibold ${s.r_realized > 0 ? "text-[color:var(--ws-green)]" : s.r_realized < 0 ? "text-[color:var(--ws-red)]" : "text-[color:var(--ws-muted)]"}`}>
                      {rightTxt}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Chat area */}
      <div className="ws-tg-chat" data-testid="vip-messages">
        <div className="space-y-4">
          {data.messages.map((m) => <MsgBubble key={m.msg_id} m={m} isAdmin={data.is_admin} onReact={react} onDelete={del} onVote={vote} onOpenImage={setLightbox} />)}
          <div ref={endRef} />
        </div>
      </div>

      {/* Composer / locked bar */}
      {data.is_admin ? (
        <div className="ws-tg-composer-wrap">
          {/* Poll builder */}
          {pollOpen && (
            <div className="ws-tg-pollbuilder" data-testid="vip-poll-builder">
              <div className="flex items-center justify-between mb-2"><span className="text-[12px] font-semibold text-white flex items-center gap-1.5"><BarChart3 className="w-4 h-4" /> Create poll</span><button onClick={() => setPollOpen(false)}><X className="w-4 h-4 text-[color:var(--ws-muted)]" /></button></div>
              <input value={pollQ} onChange={(e) => setPollQ(e.target.value)} placeholder="Ask a question…" data-testid="poll-question" className="ws-tg-input mb-2" />
              {pollOpts.map((o, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <input value={o} onChange={(e) => setPollOpts((p) => p.map((x, j) => j === i ? e.target.value : x))} placeholder={`Option ${i + 1}`} data-testid={`poll-opt-input-${i}`} className="ws-tg-input" />
                  {pollOpts.length > 2 && <button onClick={() => setPollOpts((p) => p.filter((_, j) => j !== i))}><X className="w-4 h-4 text-[color:var(--ws-muted)]" /></button>}
                </div>
              ))}
              <div className="flex items-center justify-between">
                {pollOpts.length < 6 ? <button onClick={() => setPollOpts((p) => [...p, ""])} className="text-[12px] text-[color:var(--ws-blue-bright)] inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add option</button> : <span />}
                <button onClick={sendPoll} data-testid="poll-send" className="ws-btn-primary px-4 py-1.5 rounded-lg text-[12px] font-semibold">Post poll</button>
              </div>
            </div>
          )}
          {/* Emoji picker */}
          {emojiOpen && (
            <div className="ws-tg-emoji" data-testid="vip-emoji">
              {EMOJIS.map((e, i) => <button key={i} onClick={() => { setDraft((d) => d + e); }} className="ws-tg-emoji-btn">{e}</button>)}
            </div>
          )}
          {/* Attach menu */}
          {attachOpen && (
            <div className="ws-tg-attach" data-testid="vip-attach">
              <button onClick={() => imgIn.current?.click()} className="ws-tg-attach-item" data-testid="attach-image"><ImageIcon className="w-4 h-4 text-[#38bdf8]" /> Image</button>
              <button onClick={() => vidIn.current?.click()} className="ws-tg-attach-item" data-testid="attach-video"><Video className="w-4 h-4 text-[#c4b5fd]" /> Video</button>
              <button onClick={() => docIn.current?.click()} className="ws-tg-attach-item" data-testid="attach-document"><FileText className="w-4 h-4 text-[#34d399]" /> Document</button>
              <button onClick={startRec} className="ws-tg-attach-item" data-testid="attach-voice"><Mic className="w-4 h-4 text-[#f87171]" /> Voice</button>
              <button onClick={() => { setPollOpen(true); setAttachOpen(false); }} className="ws-tg-attach-item" data-testid="attach-poll"><BarChart3 className="w-4 h-4 text-[#e6c25a]" /> Poll</button>
            </div>
          )}
          <div className="ws-tg-composer" data-testid="vip-composer">
            <button onClick={openPositions} title="Give entry from your MT5 account" data-testid="vip-give-entry" className="ws-tg-ic" style={{ color: "#e6c25a" }}><TrendingUp className="w-5 h-5" /></button>
            <button onClick={testPush} title="Send test alert to yourself" data-testid="vip-test-push" className="ws-tg-ic"><BellRing className="w-5 h-5" /></button>
            <button onClick={() => { setAttachOpen((o) => !o); setEmojiOpen(false); }} data-testid="vip-attach-btn" className="ws-tg-ic"><Paperclip className="w-5 h-5" /></button>
            <button onClick={() => { setEmojiOpen((o) => !o); setAttachOpen(false); }} data-testid="vip-emoji-btn" className="ws-tg-ic"><Smile className="w-5 h-5" /></button>
            {recording ? (
              <div className="flex-1 flex items-center gap-2 text-[13px] text-[#f87171]"><span className="ws-live-dot" style={{ background: "#f87171" }} /> Recording… <button onClick={stopRec} className="ws-btn-primary px-3 py-1 rounded-lg text-[12px] inline-flex items-center gap-1 ml-auto"><Square className="w-3 h-3" /> Stop &amp; send</button></div>
            ) : (
              <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Broadcast a message…" data-testid="vip-composer-input" className="flex-1 bg-transparent outline-none text-[14px] text-[color:var(--ws-text)] placeholder:text-[color:var(--ws-muted)]" />
            )}
            {!recording && <button onClick={send} disabled={sending} data-testid="vip-send" className="ws-btn-primary w-9 h-9 rounded-full flex items-center justify-center shrink-0">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>}
          </div>
          <input ref={imgIn} type="file" accept="image/*" hidden onChange={() => pickFile(imgIn, "image")} />
          <input ref={vidIn} type="file" accept="video/*" hidden onChange={() => pickFile(vidIn, "video")} />
          <input ref={docIn} type="file" hidden onChange={() => pickFile(docIn, "document")} />
        </div>
      ) : (
        <div className="ws-tg-locked" data-testid="vip-locked">
          <Lock className="w-4 h-4 text-[color:var(--ws-muted)]" />
          <span>Only the channel owner &amp; moderators can post. Subscribe and turn on 🔔 notifications to never miss a call.</span>
        </div>
      )}

      {posOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(3,6,14,0.75)", backdropFilter: "blur(4px)" }} data-testid="give-entry-modal" onClick={() => setPosOpen(false)}>
          <div className="w-full max-w-[460px] rounded-2xl p-5" onClick={(e) => e.stopPropagation()} style={{ background: "var(--ws-panel,#0b1120)", border: "1px solid var(--ws-border-strong,rgba(148,163,184,0.25))", boxShadow: "0 24px 70px rgba(0,0,0,0.6)" }}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-[15px] font-bold text-[color:var(--ws-text)] inline-flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#e6c25a]" /> Give an entry</h3>
              <button onClick={() => setPosOpen(false)} data-testid="give-entry-close" className="ws-tg-ic w-7 h-7"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-[12px] text-[color:var(--ws-muted)] mb-4">Pick a live position from your MT5 account — it posts to VIP with the exact entry, TP &amp; SL.</p>
            <button onClick={toggleAutoGive} data-testid="vip-auto-give" className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl mb-4" style={{ background: autoGive ? "rgba(90,169,230,0.12)" : "rgba(10,17,33,0.7)", border: `1px solid ${autoGive ? "rgba(90,169,230,0.4)" : "rgba(148,163,184,0.12)"}` }}>
              <span className="flex items-center gap-2 text-[12px] font-semibold text-[color:var(--ws-text)]"><Zap className="w-4 h-4" style={{ color: autoGive ? "#5aa9e6" : "var(--ws-muted)" }} fill={autoGive ? "currentColor" : "none"} /> Auto-Give — post every new MT5 entry automatically</span>
              <span className="w-9 h-5 rounded-full relative transition-colors" style={{ background: autoGive ? "#5aa9e6" : "rgba(148,163,184,0.35)" }}><span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all" style={{ left: autoGive ? "18px" : "2px" }} /></span>
            </button>
            {positions === null && <div className="py-8 text-center text-[color:var(--ws-muted)] text-[13px]"><Loader2 className="w-5 h-5 animate-spin inline" /> Loading your positions…</div>}
            {positions !== null && positions.length === 0 && <div className="py-8 text-center text-[13px] text-[color:var(--ws-muted)]">No open positions in your MT5 account right now.</div>}
            <div className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
              {(positions || []).map((p) => {
                const isBuy = String(p.type ?? "").toLowerCase().includes("buy") || String(p.type) === "0" || String(p.type).toLowerCase() === "long";
                return (
                  <div key={p.ticket} className="flex items-center gap-3 p-3 rounded-xl" data-testid={`give-pos-${p.ticket}`} style={{ background: "rgba(10,17,33,0.7)", border: "1px solid rgba(148,163,184,0.12)" }}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[13px] text-[color:var(--ws-text)]">{(p.symbol || "").split(":").pop()}</span>
                        <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded ${isBuy ? "bg-[rgba(34,197,94,0.2)] text-[#4ade80]" : "bg-[rgba(239,68,68,0.2)] text-[#f87171]"}`}>{isBuy ? "BUY" : "SELL"}</span>
                        {p.volume != null && <span className="text-[10px] text-[color:var(--ws-muted)]">{p.volume} lot</span>}
                      </div>
                      <div className="text-[11px] text-[color:var(--ws-muted)] mt-0.5">
                        Entry {p.price_open ?? "—"} · <span className="text-[#4ade80]">TP {p.tp || "—"}</span> · <span className="text-[#f87171]">SL {p.sl || "—"}</span>
                      </div>
                    </div>
                    <button onClick={() => givePosition(p)} disabled={posBusy === p.ticket} data-testid={`give-pos-btn-${p.ticket}`} className="ws-btn-primary px-3 py-1.5 rounded-lg text-[12px] font-bold shrink-0 inline-flex items-center gap-1.5 disabled:opacity-60">
                      {posBusy === p.ticket ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Give
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {lightbox && (
        <div className="ws-tg-lightbox" onClick={() => setLightbox(null)} data-testid="vip-lightbox">
          <button className="ws-tg-lightbox-x" onClick={() => setLightbox(null)} data-testid="vip-lightbox-close"><X className="w-6 h-6" /></button>
          <img src={lightbox} alt="preview" className="ws-tg-lightbox-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default WSVIPCommunity;
